import { useContext, useEffect, useState, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { ExperienceContext } from '~/@thd-nucleus/experience-context';
import getProductQuery, { queryRequiresStoreId, queryRequiresZipCode } from '../queries/product/getProductQuery';
import DataProviderContext from '../react/DataProviderContext';
import { getBatchedDomains, hasAllDomains } from '../react/util';
import QueryContext from '../react/QueryContext';
import { useQuery } from './useQuery';

const useProductApi = ({
  itemId,
  storeId,
  itemIds,
  zipCode,
  domains,
  batchDomains = true,
  ssr = true,
  lazy = false,
  skip,
  fetchPolicy,
  returnPartialData = false,
  alias = 'default',
  useDataSource = true,
  response
}) => {
  const context = useContext(DataProviderContext);
  const queryContext = useContext(QueryContext);

  // an extra query will go off for fbr since the anchor item
  // is inside the list of items. in this case we dont want to batch non catalog queries

  const batchedDomains = batchDomains
  && context.itemId === itemId
  && (queryContext.dataSource === 'catalog' || !queryContext.dataSource)
    ? getBatchedDomains(domains, context.batchedDomains.product, ssr)
    : domains;

  const Query = getProductQuery({ domains: batchedDomains, multiple: !!itemIds, alias, skip: response });

  const ctx = useContext(ExperienceContext);

  const sId = storeId || context.storeId || '8119';

  const productVariables = {
    itemId,
  };

  if (sId) {
    productVariables.storeId = sId;
  }

  if (queryRequiresZipCode({ domains: batchedDomains }) && zipCode) {
    productVariables.zipCode = zipCode;
  }

  if (useDataSource && queryContext.dataSource) {
    productVariables.dataSource = queryContext.dataSource;
  }

  const opts = {
    ssr,
    skip: skip || !!response || (!ssr && sId === '8119') || (!itemId && !itemIds),
    returnPartialData,
    // ac3 may have some issues with netork-only sticking across hooks
    fetchPolicy: context.fetchPolicy || 'cache-first',
    errorPolicy: 'all',
    displayName: 'alias',
    variables: productVariables,
    context: { timeout: 3000 }
  };

  if (itemIds && itemIds.length) {
    delete opts.variables.itemId;
    opts.variables.itemIds = itemIds;
  }

  if (lazy) {
    return useLazyQuery(Query, opts);
  }
  const resp = useQuery(Query, opts);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS && resp.data && !resp.loading) {
      const { data } = resp;

      window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.data', {
        type: 'product',
        variables: opts.variables,
        data
      });
    }
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS && resp.error && !resp.loading) {
      const { data } = resp;

      window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.error', {
        type: 'product',
        variables: opts.variables,
        data
      });
    }
  }, [resp.loading]);

  if (resp.data?.product) {
    if (context?.productCache) {
      context.productCache.addProduct({ product: resp.data.product, storeId: productVariables.storeId });
    }
  }

  if (response) {
    response.variables = {
      ...productVariables
    };
  }

  return response || resp;

};

const useProduct = ({ itemId, storeId, domains, ...props }) => {
  const context = useContext(DataProviderContext);
  const queryContext = useContext(QueryContext);
  const sId = storeId || context.storeId || '8119';
  const { dataSource } = queryContext || {};
  let response;
  if (itemId) {
    if (context?.productCache) {
      let product = {
        itemId,
        dataSources: dataSource
      };
      product = context.productCache.getProduct({
        product,
        storeId: sId,
        dataSource
      });

      if (hasAllDomains({
        product,
        domains
      })) {
        response = {
          data: {
            model: {
              product
            },
            product
          },
          loading: false,
          fromInMemoryCache: true
        };
      }
    }
  }

  return useProductApi({
    itemId, storeId, domains, ...props, response
  });
};

export default useProduct;
